if (!customElements.get('horizontal-card-slider')) {
  class HorizontalCardSlider extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedOnce) return;

      this.isConnectedOnce = true;
      this.viewport = this.querySelector('[data-horizontal-card-slider-viewport]');
      this.pagination = this.querySelector('[data-horizontal-card-slider-pagination]');
      this.paginationContainer = this.querySelector('[data-horizontal-card-slider-pagination-container]');
      this.navigation = this.querySelector('[data-horizontal-card-slider-navigation]');
      this.previousButton = this.querySelector('[data-horizontal-card-slider-previous]');
      this.nextButton = this.querySelector('[data-horizontal-card-slider-next]');
      this.slides = Array.from(this.querySelectorAll('[data-horizontal-card-slider-slide]'));
      this.desktopSlides = this.getSlideCount(this.dataset.desktopSlides, 3);
      this.mobileSlides = this.getSlideCount(this.dataset.mobileSlides, 1.2);
      this.enableLoop = this.dataset.enableLoop === 'true';
      this.paginationStyle = this.dataset.paginationStyle;
      this.showDesktopNavigation = this.dataset.showDesktopNavigation === 'true';
      this.breakpoint = window.matchMedia('(min-width: 750px)');

      this.handlePreviousBound = this.handlePrevious.bind(this);
      this.handleNextBound = this.handleNext.bind(this);
      this.handleBreakpointChangeBound = this.handleBreakpointChange.bind(this);
      this.handleBlockSelectBound = this.handleBlockSelect.bind(this);
      this.handleWindowLoadBound = this.initSwiper.bind(this);

      this.previousButton?.addEventListener('click', this.handlePreviousBound);
      this.nextButton?.addEventListener('click', this.handleNextBound);
      this.breakpoint.addEventListener('change', this.handleBreakpointChangeBound);
      this.addEventListener('shopify:block:select', this.handleBlockSelectBound);

      if (typeof ResizeObserver !== 'undefined' && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => {
          this.swiper?.update();
          this.syncControls();
        });
        this.resizeObserver.observe(this.viewport);
      }

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', this.handleWindowLoadBound, { once: true });
      } else {
        this.initSwiper();
      }
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.handlePreviousBound);
      this.nextButton?.removeEventListener('click', this.handleNextBound);
      this.breakpoint?.removeEventListener('change', this.handleBreakpointChangeBound);
      this.removeEventListener('shopify:block:select', this.handleBlockSelectBound);
      window.removeEventListener('load', this.handleWindowLoadBound);
      this.resizeObserver?.disconnect();
      this.destroySwiper();
      this.isConnectedOnce = false;
    }

    getSlideCount(value, fallback) {
      const count = Number.parseFloat(value);
      return Number.isFinite(count) && count > 0 ? count : fallback;
    }

    getViewportSettings() {
      return this.breakpoint.matches
        ? { slidesPerView: this.desktopSlides, spaceBetween: 20 }
        : { slidesPerView: this.mobileSlides, spaceBetween: 15 };
    }

    canLoop(slidesPerView) {
      return this.enableLoop && this.slides.length >= Math.ceil(slidesPerView) + 1;
    }

    initSwiper(initialIndex = 0) {
      if (this.swiper || !this.viewport || !this.slides.length || typeof window.Swiper === 'undefined') return;

      this.viewportSettings = this.getViewportSettings();
      this.loopActive = this.canLoop(this.viewportSettings.slidesPerView);

      const pagination = this.getPaginationOptions();
      this.swiper = new window.Swiper(this.viewport, {
        slidesPerView: this.viewportSettings.slidesPerView,
        slidesPerGroup: 1,
        spaceBetween: this.viewportSettings.spaceBetween,
        loop: this.loopActive,
        watchOverflow: true,
        grabCursor: true,
        observer: true,
        observeParents: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
        },
        pagination,
        on: {
          init: () => this.syncControls(),
          update: () => this.syncControls(),
          resize: () => this.syncControls(),
          lock: () => this.syncControls(),
          unlock: () => this.syncControls(),
          slideChange: () => this.syncControls(),
        },
      });

      if (this.loopActive) {
        this.swiper.slideToLoop(initialIndex, 0, false);
      } else {
        this.swiper.slideTo(initialIndex, 0, false);
      }

      this.syncControls();
    }

    getPaginationOptions() {
      if (!this.pagination || this.isPaginationStyleDisabled()) return undefined;

      return {
        el: this.pagination,
        type: this.paginationStyle === 'progress' ? 'progressbar' : 'bullets',
        clickable: this.paginationStyle === 'dots',
      };
    }

    isPaginationStyleDisabled() {
      return this.paginationStyle === 'none' || this.paginationStyle === 'hidden';
    }

    shouldHidePagination() {
      // 桌面端导航与分页器仅在视觉上互斥，Swiper 仍保留分页模块以稳定初始化拖动交互。
      return this.isPaginationStyleDisabled() || (this.breakpoint.matches && this.showDesktopNavigation);
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    handleBreakpointChange() {
      const activeIndex = this.swiper?.realIndex ?? 0;

      // 循环资格依赖当前端侧的每屏数量，跨断点时需重建实例。
      this.destroySwiper();
      this.initSwiper(activeIndex);
    }

    handlePrevious() {
      this.swiper?.slidePrev();
    }

    handleNext() {
      this.swiper?.slideNext();
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-horizontal-card-slider-slide]');
      const index = Number.parseInt(selectedSlide?.dataset.slideIndex, 10);

      if (!this.swiper || Number.isNaN(index)) return;

      if (this.loopActive) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
    }

    syncControls() {
      if (!this.swiper || !this.viewportSettings) return;

      const hasOverflow = this.slides.length > this.viewportSettings.slidesPerView;

      if (this.paginationContainer) {
        this.paginationContainer.hidden = !hasOverflow || this.shouldHidePagination();
      }

      if (this.navigation) {
        this.navigation.hidden = !hasOverflow;
      }

      if (!hasOverflow || this.loopActive) {
        this.setNavigationState(false, false);
        return;
      }

      this.setNavigationState(this.swiper.isBeginning, this.swiper.isEnd);
    }

    setNavigationState(previousDisabled, nextDisabled) {
      if (this.previousButton) {
        this.previousButton.disabled = previousDisabled;
        this.previousButton.setAttribute('aria-disabled', String(previousDisabled));
      }

      if (this.nextButton) {
        this.nextButton.disabled = nextDisabled;
        this.nextButton.setAttribute('aria-disabled', String(nextDisabled));
      }
    }
  }

  customElements.define('horizontal-card-slider', HorizontalCardSlider);
}
